import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { PATH } from '../../utility/path'
import { TEXT } from '../../utility/text'
import './sidebar.scss'
import { Icon } from '../icon'
import { PermissionKey, PermissionProvider } from '../../context'

export const Sidebar = () => {
  const { pathname, search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const fromSection = searchParams.get('from')
  const fromAnalysis = fromSection === 'analysis'
  const fromUsers = fromSection === 'users'
  const fromCe = fromSection === 'ce'
  const fromStreams = fromSection === 'streams'
  const fromEntities = fromSection === 'entities'
  const fromSharedGroups = fromSection === 'shared-groups'
  const isOnMembership = !!pathname.match(PATH.admin.ad_management.membership)
  const [isOpenFeeCalculation, setIsOpenFeeCalculation] = useState(true)
  const [isOpenDataInput, setIsDataInput] = useState(true)
  const [isOpenAdmin, setIsOpenAdmin] = useState(true)
  const [isOpenADManagement, setIsOpenADManagement] = useState(true)
  const [isOpenPatientSource, setIsOpenPatientSource] = useState(true)

  return (
    <aside className='f f-d-c bg-base100'>
      <nav className='nav f f-d-c p-y-sm'>
        <PermissionProvider permission={PermissionKey.mn_home}>
          <NavLink
            className='nav__unit nav__unit--level-1 t-s-md t-w-500'
            end
            to={PATH.home}
            data-testid='menu-home'
          >
            <Icon name='Home' />
            {TEXT.menu.home}
          </NavLink>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_hrsa_audit}>
          <NavLink
            className={() =>
              pathname.startsWith(PATH.activity)
                ? 'nav__unit nav__unit--level-1 t-s-md t-w-500 active'
                : 'nav__unit nav__unit--level-1 t-s-md t-w-500'
            }
            end
            to={PATH.activity}
            data-testid='menu-activity'
          >
            <Icon
              name='Activity'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            />
            {TEXT.menu.tpa_activities}
          </NavLink>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_upload}>
          <NavLink
            className='nav__unit nav__unit--level-1 t-s-md t-w-500'
            to={PATH.upload.upload}
            data-testid='menu-upload'
          >
            <Icon name='Upload' />
            {TEXT.menu.upload}
          </NavLink>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_data_input}>
          <div
            className='nav__unit nav__trigger nav__unit--level-1 t-s-md t-w-500'
            onClick={() => setIsDataInput(!isOpenDataInput)}
            data-testid='menu-data-input'
          >
            <Icon name='Database' />
            {TEXT.menu.data_input}
            <Icon
              name={isOpenDataInput ? 'ArrowBottom' : 'ArrowRight'}
              className='m-r-md m-l-auto'
            />
          </div>
          <div className={`nav__content ${isOpenDataInput ? 'is-active' : ''}`}>
            <PermissionProvider permission={PermissionKey.mn_npi_eligibility}>
              <NavLink
                className='nav__unit nav__unit--level-3 t-s-md t-w-500'
                to={PATH.data_input.npi_eligibility}
                data-testid='menu-npi-eligibility'
              >
                {TEXT.menu.npi_eligibility}
              </NavLink>
            </PermissionProvider>
          </div>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_patient_source}>
          <div
            className='nav__unit nav__trigger nav__unit--level-1 t-s-md t-w-500'
            onClick={() => setIsOpenPatientSource(!isOpenPatientSource)}
            data-testid='menu-patient-source'
          >
            <Icon
              name='UserCheck'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            />
            {TEXT.menu.patient_source}
            <Icon
              name={isOpenPatientSource ? 'ArrowBottom' : 'ArrowRight'}
              className='m-r-md m-l-auto'
            />
          </div>
          <div className={`nav__content ${isOpenPatientSource ? 'is-active' : ''}`}>
            <PermissionProvider permission={PermissionKey.mn_source_mngt}>
              <NavLink
                className='nav__unit nav__unit--level-3 t-s-md t-w-500'
                to={PATH.patient_source.source_management}
                data-testid='menu-source_management'
              >
                {TEXT.menu.source_management}
              </NavLink>
            </PermissionProvider>
          </div>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_switch_mappings}>
          <NavLink
            className={() =>
              pathname.startsWith(PATH.switch)
                ? 'nav__unit nav__unit--level-1 t-s-md t-w-500 active'
                : 'nav__unit nav__unit--level-1 t-s-md t-w-500'
            }
            end
            to={PATH.switch}
            data-testid='menu-switch-mappings'
          >
            <Icon
              name='Switch'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            />
            {TEXT.menu.switch}
          </NavLink>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_fee_calc}>
          <div
            className='nav__unit nav__trigger nav__unit--level-1 t-s-md t-w-500'
            onClick={() => setIsOpenFeeCalculation(!isOpenFeeCalculation)}
            data-testid='menu-fee-calculation'
          >
            <Icon name='Range' />
            {TEXT.menu.fee_calculation}
            <Icon
              name={isOpenFeeCalculation ? 'ArrowBottom' : 'ArrowRight'}
              className='m-r-md m-l-auto'
            />
          </div>
          <div className={`nav__content ${isOpenFeeCalculation ? 'is-active' : ''}`}>
            <PermissionProvider permission={PermissionKey.mn_field_conf}>
              <NavLink
                className='nav__unit nav__unit--level-3 t-s-md t-w-500'
                to={PATH.fee_calculation.field_configuration.flags}
                data-testid='menu-field-configuration'
              >
                {TEXT.menu.field_configuration}
              </NavLink>
            </PermissionProvider>
            <PermissionProvider permission={PermissionKey.mn_fee_mngt}>
              <NavLink
                className='nav__unit nav__unit--level-3 t-s-md t-w-500'
                to={PATH.fee_calculation.fee_management.fees}
                data-testid='menu-fee-management'
              >
                {TEXT.menu.fee_management}
              </NavLink>
            </PermissionProvider>
          </div>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_support}>
          <NavLink
            className='nav__unit nav__unit--level-1 t-s-md t-w-500'
            to={PATH.support}
            data-testid='menu-support'
          >
            <Icon name='Support' />
            {TEXT.menu.support}
          </NavLink>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_admin}>
          <div
            className='nav__unit nav__trigger nav__unit--level-1 t-s-md t-w-500'
            onClick={() => setIsOpenAdmin(!isOpenAdmin)}
            data-testid='menu-admin'
          >
            <Icon name='User' />
            {TEXT.menu.admin}
            <Icon
              name={isOpenAdmin ? 'ArrowBottom' : 'ArrowRight'}
              className='m-r-md m-l-auto'
            />
          </div>
          <div className={`nav__content ${isOpenAdmin ? 'is-active' : ''}`}>
            <PermissionProvider permission={PermissionKey.mn_sync}>
              <NavLink
                className='nav__unit nav__unit--level-3 t-s-md t-w-500'
                to={PATH.admin.synchronization}
                data-testid='menu-synchronization'
              >
                {TEXT.menu.synchronization}
              </NavLink>
            </PermissionProvider>
            <PermissionProvider permission={PermissionKey.mn_user_roles_mngt}>
              <NavLink
                className='nav__unit nav__unit--level-3 t-s-md t-w-500'
                to={PATH.admin.user_roles_management.permissions}
                data-testid='menu-user-role-management'
              >
                {TEXT.menu.user_roles_management}
              </NavLink>
            </PermissionProvider>
            <PermissionProvider permission={PermissionKey.mn_aux_rules_mngt}>
              <NavLink
                className='nav__unit nav__unit--level-3 t-s-md t-w-500'
                to={PATH.admin.aux_rules_management.validation_rules}
                data-testid='menu-aux-rules-management'
              >
                {TEXT.menu.aux_rules_management}
              </NavLink>
            </PermissionProvider>
            <PermissionProvider permission={PermissionKey.mn_category_classification}>
              <NavLink
                className={() =>
                  pathname.startsWith(PATH.admin.category_classifications) || pathname.startsWith(PATH.admin.category_classification)
                    ? 'nav__unit nav__unit--level-3 t-s-md t-w-500 active'
                    : 'nav__unit nav__unit--level-3 t-s-md t-w-500'
                }
                to={PATH.admin.category_classifications}
                data-testid='menu-category-classifications'
              >
                {TEXT.menu.category_classification}
              </NavLink>
            </PermissionProvider>
            <PermissionProvider permission={PermissionKey.mn_ad_mngt}>
              <div
                className='nav__unit nav__trigger nav__unit--level-3 t-s-md t-w-500'
                onClick={() => setIsOpenADManagement(!isOpenADManagement)}
                data-testid='menu-ad_management'
              >
                {TEXT.menu.ad_management}
                <Icon
                  name={isOpenADManagement ? 'ArrowBottom' : 'ArrowRight'}
                  className='m-r-md m-l-auto'
                />
              </div>
              <div className={`nav__content ${isOpenADManagement ? 'is-active' : ''}`}>
                <PermissionProvider permission={PermissionKey.mn_users}>
                  <NavLink
                    className={`nav__unit nav__unit--level-4 t-s-md t-w-500${
                      (pathname.match(PATH.admin.ad_management.user) && !fromAnalysis) ||
                      (pathname.match(PATH.admin.ad_management.membership) && fromUsers)
                        ? ' active'
                        : ''
                    }`}
                    to={PATH.admin.ad_management.users}
                    data-testid='menu-users'
                  >
                    {TEXT.menu.users}
                  </NavLink>
                </PermissionProvider>
                <PermissionProvider permission={PermissionKey.mn_ce}>
                  <NavLink
                    className={`nav__unit nav__unit--level-4 t-s-md t-w-500${
                      pathname.match(PATH.admin.ad_management.covered_entity) || (isOnMembership && fromCe) ? ' active' : ''
                    }`}
                    to={PATH.admin.ad_management.covered_entities}
                    data-testid='menu-covered-entities'
                  >
                    {TEXT.menu.covered_entities}
                  </NavLink>
                </PermissionProvider>
                <PermissionProvider permission={PermissionKey.mn_memberships}>
                  <NavLink
                    className={`nav__unit nav__unit--level-4 t-s-md t-w-500${
                      isOnMembership && !fromAnalysis && !fromUsers && !fromCe && !fromStreams && !fromEntities && !fromSharedGroups
                        ? ' active'
                        : ''
                    }`}
                    to={PATH.admin.ad_management.memberships}
                    data-testid='menu-memberships'
                  >
                    {TEXT.menu.memberships}
                  </NavLink>
                </PermissionProvider>
                <PermissionProvider permission={PermissionKey.mn_streams_apps}>
                  <NavLink
                    className={`nav__unit nav__unit--level-4 t-s-md t-w-500${
                      pathname.match(PATH.admin.ad_management.streams_and_apps) || (isOnMembership && fromStreams) ? ' active' : ''
                    }`}
                    to={PATH.admin.ad_management.streams_and_apps}
                    data-testid='menu-streams-and-apps'
                  >
                    {TEXT.menu.streams_and_apps}
                  </NavLink>
                </PermissionProvider>
                <PermissionProvider permission={PermissionKey.mn_shared_groups}>
                  <NavLink
                    className={`nav__unit nav__unit--level-4 t-s-md t-w-500${
                      pathname.match(PATH.admin.ad_management.shared_groups) || (isOnMembership && fromSharedGroups) ? ' active' : ''
                    }`}
                    to={PATH.admin.ad_management.shared_groups}
                    data-testid='menu-shared-groups'
                  >
                    {TEXT.menu.shared_groups}
                  </NavLink>
                </PermissionProvider>
                <PermissionProvider permission={PermissionKey.mn_entities}>
                  <NavLink
                    className={`nav__unit nav__unit--level-4 t-s-md t-w-500${
                      pathname.match(PATH.admin.ad_management.entities) || (isOnMembership && fromEntities) ? ' active' : ''
                    }`}
                    to={PATH.admin.ad_management.entities}
                    data-testid='menu-entities'
                  >
                    {TEXT.menu.entities}
                  </NavLink>
                </PermissionProvider>
                <PermissionProvider permission={PermissionKey.mn_pp_roles}>
                  <NavLink
                    className={`nav__unit nav__unit--level-4 t-s-md t-w-500${
                      pathname.match(PATH.admin.ad_management.pp_roles) ? ' active' : ''
                    }`}
                    to={PATH.admin.ad_management.pp_roles}
                    data-testid='menu-pp-roles'
                  >
                    {TEXT.menu.pp_roles}
                  </NavLink>
                </PermissionProvider>
                <PermissionProvider permission={PermissionKey.mn_ad_analysis}>
                  <NavLink
                    className={`nav__unit nav__unit--level-4 t-s-md t-w-500${
                      pathname.match(PATH.admin.ad_management.analysis) || fromAnalysis ? ' active' : ''
                    }`}
                    to={PATH.admin.ad_management.analysis}
                    data-testid='menu-analysis'
                  >
                    {TEXT.menu.analysis}
                  </NavLink>
                </PermissionProvider>
              </div>
            </PermissionProvider>
          </div>
        </PermissionProvider>
        <PermissionProvider permission={PermissionKey.mn_pharmasee}>
          <NavLink
            className='nav__unit nav__unit--level-1 t-s-md t-w-500'
            to={PATH.pharmasee}
            data-testid='menu-pharmasee'
          >
            <Icon name='Pharmasee' />
            {TEXT.menu.pharmasee}
          </NavLink>
        </PermissionProvider>
      </nav>
      <footer>
        <div className='t-s-xs t-c-base700 t-w-600'>© Copyright {new Date().getFullYear()} PharmalytIQ, LLC. All Rights Reserved.</div>
      </footer>
    </aside>
  )
}
